import { FlyToInterpolator, type MapViewState, WebMercatorViewport } from "@deck.gl/core";
import { IconLayer, PolygonLayer, ScatterplotLayer } from "@deck.gl/layers";
import DeckGL from "@deck.gl/react";
import LabelIcon from "@mui/icons-material/Label";
import NavigationIcon from "@mui/icons-material/Navigation";
import SettingsIcon from "@mui/icons-material/Settings";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import useTheme from "@mui/material/styles/useTheme";
import Tooltip from "@mui/material/Tooltip";
import { useQueryClient } from "@tanstack/react-query";
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import Map, { Layer, Source } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { useAssetLabels } from "../hooks/useAssetLabels";
import {
  type ClusterOrPoint,
  type ViewportBounds,
  isCluster,
  useCluster,
} from "../hooks/useCluster";
import { useMapAssetRows } from "../hooks/useMapAssetRows";
import { useMapGroupStyles } from "../hooks/useMapGroupStyles";
import { useMapSettings } from "../hooks/useMapSettings";
import {
  AssetLabelBadge,
  POWERTRAIN_LABELS,
} from "../models/assetLabelBadge";
import { boundsOfPositions, viewportForBounds } from "../models/fitBounds";
import { groupSymbolDataUrl, recolorGroupSymbolSvgCached } from "../models/groupSymbolSvg";
import { DEFAULT_MAP_VIEWPORT, groupStyleOf, hexToRgba } from "../models/mapSettings";
import { MAP_STYLES } from "../models/mapStyles";
import { HAS_TRAFFIC_PROXY, TRAFFIC_LAYER_KEYS, trafficTileUrl } from "../models/trafficNrw";
import { ZoneOutline } from "../models/zoneContainment";
import { AssetLabelOverlay } from "./AssetLabelOverlay";
import { MapControlStack } from "./MapControlStack";
import MapSearchBar from "./MapSearchBar";
import { MapSettingsPanel } from "./MapSettingsPanel";
import { useAnimatedPositions } from "./useAnimatedPositions";
import { vehicleIconAngle } from "./vehicleIconAngle";
import VehicleInfoPanel from "./VehicleInfoPanel";
import { VehicleListPanel } from "./VehicleListPanel";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { MAP_DIAGNOSTICS } from "@/constants/constants";
import { useDevicesByIds } from "@/hooks/devices/useDevicesByIds";
import { useFuelCellDeviceIds } from "@/hooks/devices/useFuelCellDeviceIds";
import useLastKnownPositions from "@/hooks/positions/useLastKnownPositions";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { useZoneOutlines } from "@/hooks/zone/useZoneOutlines";
import { clamp } from "@/utilities/clamp";

// ─── Static SVG icons ──────────────────────────────────────────────

function svgToDataURL(svgMarkup: string) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
}

const NAVIGATION_PATH = "M12 2 4.5 20.29l.71.71L12 18l6.79 3 .71-.71z";
const SQUARE_ROUNDED_PATH =
  "M3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2";

/** An icon as deck.gl wants it. Kept whole so the same object is handed back every frame. */
interface MapIcon {
  url: string;
  width: number;
  height: number;
}

/**
 * How many built icons each cache holds on to.
 *
 * A colour input reports a new colour on every movement of the pointer, so one drag through the
 * picker asks for dozens of colours that will never be asked for again. Kept for the lifetime of
 * the page they add up -- a recoloured group symbol can be forty kilobytes of data URL. The
 * oldest is dropped once the cache is full, which for a fleet drawn in a handful of colours
 * never happens.
 */
const MAX_CACHED_ICONS = 64;

/** Drops the oldest entries once a cache has grown past {@link MAX_CACHED_ICONS}. */
function capCache(cache: globalThis.Map<string, unknown>) {
  for (const key of cache.keys()) {
    if (cache.size <= MAX_CACHED_ICONS) break;
    cache.delete(key);
  }
}

/**
 * The vehicle icons in one colour.
 *
 * Cached per colour because a fleet coloured by group asks for the same handful of colours over
 * and over, and each miss builds two data URLs.
 */
const vehicleIconCache = new globalThis.Map<string, { driving: MapIcon; stopped: MapIcon }>();
function getVehicleIcons(color: string) {
  const cached = vehicleIconCache.get(color);
  if (cached) return cached;

  const { driving, stopped } = buildIconMapping(color);
  const icons = {
    driving: { url: driving, width: 128, height: 128 },
    stopped: { url: stopped, width: 128, height: 128 },
  };
  vehicleIconCache.set(color, icons);
  capCache(vehicleIconCache);
  return icons;
}

/**
 * An uploaded group symbol as an icon, cached so the same markup is not turned into a URL again
 * on every frame.
 */
const groupSymbolCache = new globalThis.Map<string, MapIcon>();
function groupSymbolIcon(svg: string, color: string): MapIcon {
  const cacheKey = `${color}::${svg}`;
  const cached = groupSymbolCache.get(cacheKey);
  if (cached) return cached;

  const icon = {
    url: groupSymbolDataUrl(recolorGroupSymbolSvgCached(svg, color)),
    width: 128,
    height: 128,
  };
  groupSymbolCache.set(cacheKey, icon);
  capCache(groupSymbolCache);
  return icon;
}

/** A small triangle, drawn pointing up so a rotation can aim it anywhere. */
const HEADING_MARKER_PATH = "M12 3 4 21h16z";
const headingMarkerCache = new globalThis.Map<string, MapIcon>();

/**
 * The arrow that shows which way a vehicle is going when its symbol does not turn.
 *
 * A group's own symbol is drawn as its author drew it, upright — a lorry lying on its side is not
 * a heading. So the heading moves out to the edge of the ring as a separate mark.
 */
function getHeadingMarker(color: string): MapIcon {
  const cached = headingMarkerCache.get(color);
  if (cached) return cached;

  const icon = {
    url: svgToDataURL(
      `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 24 24"><path d="${HEADING_MARKER_PATH}" fill="${color}" stroke="#fff" stroke-width="1.5"/></svg>`,
    ),
    width: 128,
    height: 128,
  };
  headingMarkerCache.set(color, icon);
  capCache(headingMarkerCache);
  return icon;
}

function buildIconMapping(color: string) {
  const driving = svgToDataURL(
    `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 24 24"><path d="${NAVIGATION_PATH}" fill="${color}"/></svg>`,
  );
  const stopped = svgToDataURL(
    `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 24 24"><path d="${SQUARE_ROUNDED_PATH}" fill="${color}"/></svg>`,
  );
  return { driving, stopped };
}

// ─── Cluster icon ──────────────────────────────────────────────────

/** Keyed by colour as well as count: the same number in two colours is two icons. */
const clusterIconCache = new globalThis.Map<string, string>();
function getClusterIcon(count: number, color: string): string {
  const cacheKey = `${color}::${count}`;
  const cached = clusterIconCache.get(cacheKey);
  if (cached) return cached;

  const size = Math.min(128, 48 + Math.log2(count) * 12);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">
    <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" opacity="0.85" stroke="#fff" stroke-width="2"/>
    <text x="${size / 2}" y="${size / 2}" text-anchor="middle" dominant-baseline="central" fill="#fff" font-size="${Math.min(size * 0.4, 24)}" font-family="Roboto, sans-serif" font-weight="500">${count}</text>
  </svg>`;
  const url = svgToDataURL(svg);
  clusterIconCache.set(cacheKey, url);
  capCache(clusterIconCache);
  return url;
}

/**
 * How big a cluster bubble is drawn, in screen pixels, for the number of vehicles in it.
 *
 * Pixels, not a size on the ground. Scaled with the zoom — which is what `sizeUnits: "common"`
 * does — an icon grows and shrinks as the map is zoomed and, clamped at both ends, changes size
 * at two arbitrary zoom levels and nowhere else. It also lets the name layout keep names off
 * these bubbles, which it can only do if the size it is told is the size that gets drawn.
 */
function clusterIconSize(count: number): number {
  return Math.min(60, 30 + Math.log2(count) * 5);
}

// ─── Types & constants ─────────────────────────────────────────────

interface ViewState extends MapViewState {
  pitch: number;
  bearing: number;
}

/**
 * Parameter shape deck.gl instantiates `onViewStateChange` with. The prop is a
 * generic callback, so the concrete type has to be read back off `DeckGL`.
 */
type ViewStateChange = Parameters<
  NonNullable<ComponentProps<typeof DeckGL>["onViewStateChange"]>
>[0];

type Coord = [number, number, string, boolean];

const INITIAL_VIEW_STATE: ViewState = DEFAULT_MAP_VIEWPORT;

const BOUNDS_SETTLE_MS = 75;
const USE_BOUNDED_POSITION_FETCH = false;
const POSITION_BOUNDS_ZOOM_THRESHOLD = 14;
const POSITION_BOUNDS_PADDING_FACTOR = 2.0;
const POSITION_REFETCH_MS = 12000;
const STATUS_INFO_REFETCH_MS = 2000;
const POSITION_ANIMATION_FALLBACK_MS = Math.round(POSITION_REFETCH_MS * 0.85);
const OVERLAP_DETECTION_PX = 26;
const OVERLAP_SPREAD_PX = 22;
const POSITION_RETENTION_MS = 30000;
const MIN_MAP_LAT = -85;
const MAX_MAP_LAT = 85;
const FOLLOW_POSITION_LERP_FACTOR = 0.16;
const FOLLOW_BEARING_LERP_FACTOR = 0.22;
const FOLLOW_POSITION_EPSILON = 0.000005;
const FOLLOW_BEARING_EPSILON = 0.2;
const DRIVING_STATE_SWITCH_DELAY_MS = 1200;
/** How long the map has to sit still before where it is looking is worth storing. */
const VIEWPORT_PERSIST_DELAY_MS = 1000;
/** Size of the arrow that sits on the ring for a vehicle whose own symbol does not turn. */
const HEADING_MARKER_SIZE_PX = 14;
/** Groups come along because a vehicle can be coloured by the group it is in. */
const DEVICE_FIELDS = ["id", "name", "serialNumber", "groups"];

/** The positions out of a set of coordinates, by device id. */
function toPositionMap(coords: Coord[]): globalThis.Map<string, [number, number]> {
  const positions = new globalThis.Map<string, [number, number]>();
  for (const [lng, lat, id] of coords) {
    positions.set(id, [lng, lat]);
  }
  return positions;
}

function normalizeLng(lng: number): number {
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}

function normalizeBearing(bearing: number): number {
  return ((bearing % 360) + 360) % 360;
}

function shortestAngleDelta(from: number, to: number): number {
  return ((to - from + 540) % 360) - 180;
}

// ─── Viewport bounds computation ──────────────────────────────────

function computeViewportBounds(
  vs: ViewState,
  width: number,
  height: number,
): ViewportBounds | null {
  if (width <= 0 || height <= 0) return null;
  const vp = new WebMercatorViewport({
    longitude: vs.longitude,
    latitude: vs.latitude,
    zoom: vs.zoom,
    pitch: vs.pitch,
    bearing: vs.bearing,
    width,
    height,
  });
  const [west, south, east, north] = vp.getBounds();
  return { west, south, east, north };
}

// ─── Fixed tooltip component ──────────────────────────────────────

interface TooltipInfo {
  x: number;
  y: number;
  isCluster: boolean;
  clusterCount?: number;
  deviceId?: string;
}

function FixedTooltip({ info, text }: { info: TooltipInfo | null; text: string | null }) {
  if (!info) return null;
  const isLoading = !info.isCluster && text === null;

  return (
    <div
      style={{
        position: "absolute",
        left: info.x,
        top: info.y - 36,
        pointerEvents: "none",
        zIndex: 1000,
        transform: "translateX(-50%)",
        background: "rgba(0,0,0,0.8)",
        color: "#fff",
        borderRadius: 4,
        padding: "4px 8px",
        fontSize: 14,
        whiteSpace: "nowrap",
        maxWidth: 300,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {isLoading ? (
        <Skeleton variant="text" width={80} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
      ) : (
        text
      )}
    </div>
  );
}

// ─── Compass button ───────────────────────────────────────────────

function CompassButton({ bearing, onClick }: { bearing: number; onClick: () => void }) {
  return (
    <Paper elevation={2} sx={{ borderRadius: "50%", overflow: "hidden" }}>
      <IconButton
        onClick={onClick}
        size="medium"
        sx={{
          width: 40,
          height: 40,
          transition: "transform 0.2s ease",
          transform: `rotate(${-bearing}deg)`,
        }}
      >
        <NavigationIcon
          sx={{
            color: "error.main",
            fontSize: 22,
          }}
        />
      </IconButton>
    </Paper>
  );
}

/**
 * A round map button that is either on or off.
 *
 * The third one of these earned it a component of its own; the compass is still on its own
 * because it turns rather than switches.
 */
function MapToggleButton({
  label,
  active,
  icon,
  onClick,
}: {
  label: string;
  /** Left out for a button that does something rather than switching something. */
  active?: boolean;
  icon: ReactNode;
  onClick: () => void;
}) {
  return (
    <Tooltip title={label} placement="left">
      <Paper elevation={2} sx={{ borderRadius: "50%", overflow: "hidden" }}>
        <IconButton
          onClick={onClick}
          size="medium"
          // The tooltip puts its label on the Paper, not on the button, so without this a screen
          // reader announces nothing but "button".
          aria-label={label}
          aria-pressed={active}
          sx={{ width: 40, height: 40 }}
        >
          {icon}
        </IconButton>
      </Paper>
    </Tooltip>
  );
}

// ─── Component ─────────────────────────────────────────────────────

const LiveMapView = () => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const { t } = useTranslation();

  // Read first: the map is drawn from these, and where it starts looking comes out of them.
  const { settings, update: updateSettings, isFetched: settingsFetched } = useMapSettings();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const groupStyles = useMapGroupStyles();
  const zoneOutlines = useZoneOutlines(settings.showZones);

  /**
   * The traffic overlays actually drawn.
   *
   * A stored choice is kept even where there is no proxy to fetch it from, so moving between
   * deployments does not silently throw it away; it just cannot be drawn here.
   */
  const trafficLayers = useMemo(
    () =>
      HAS_TRAFFIC_PROXY && settings.showTraffic
        ? // The array too, not just the address: a new one each render has MapLibre take the
          // source down and put it back up again.
          TRAFFIC_LAYER_KEYS.map(key => ({ key, tiles: [trafficTileUrl(key)] }))
        : [],
    [settings.showTraffic],
  );

  const themeColors = useMemo(
    () => ({
      icon: primaryColor,
      labelText: theme.palette.text.primary,
      labelBackground: theme.palette.background.paper,
    }),
    [primaryColor, theme.palette.background.paper, theme.palette.text.primary],
  );

  // ─── Container size ──────────────────────────────────────────────
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setContainerSize({
          width: Math.round(entry.contentRect.width),
          height: Math.round(entry.contentRect.height),
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ─── View state (controlled for fly-to support) ──────────────────
  const [viewState, setViewState] = useState<ViewState>(INITIAL_VIEW_STATE);

  /**
   * Puts the map back where the reader left it.
   *
   * Once only, and only after the stored settings have actually arrived — otherwise the defaults
   * would count as "the stored viewport" and the map would jump to the middle of Germany a moment
   * after the reader had already started panning.
   */
  const restoredViewportRef = useRef(false);
  useEffect(() => {
    if (restoredViewportRef.current || !settingsFetched) return;
    restoredViewportRef.current = true;

    const stored = settings.viewport;
    if (stored) {
      setViewState(current => ({ ...current, ...stored }));
    }
  }, [settingsFetched, settings.viewport]);

  /**
   * Remembers where the map is looking, a moment after it stops moving.
   *
   * Not on every frame of a pan: each stored record is a round trip, and the settings store
   * already waits before writing. This waits as well so a drag becomes one write, not eighty.
   */
  useEffect(() => {
    if (!restoredViewportRef.current) return;

    const timeout = setTimeout(() => {
      updateSettings({
        viewport: {
          longitude: viewState.longitude,
          latitude: viewState.latitude,
          zoom: viewState.zoom,
          pitch: viewState.pitch,
          bearing: viewState.bearing,
        },
      });
    }, VIEWPORT_PERSIST_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [viewState, updateSettings]);

  const flyTo = useCallback(
    (
      longitude: number,
      latitude: number,
      zoom = 15,
      transitionDuration = 2000,
      bearing?: number,
    ) => {
      setViewState(prev => ({
        ...prev,
        longitude,
        latitude,
        zoom,
        bearing: bearing ?? prev.bearing,
        transitionDuration,
        transitionInterpolator: new FlyToInterpolator(),
      }));
    },
    [],
  );

  // ─── Viewport bounds ───────────────────────────────────────────
  const boundsRef = useRef<ViewportBounds | null>(null);
  const boundsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [stableBounds, setStableBounds] = useState<ViewportBounds | null>(null);

  useEffect(() => {
    const newBounds = computeViewportBounds(viewState, containerSize.width, containerSize.height);
    if (!newBounds) return;

    boundsRef.current = newBounds;

    if (!stableBounds) {
      setStableBounds(newBounds);
      return;
    }

    if (boundsTimerRef.current) clearTimeout(boundsTimerRef.current);
    boundsTimerRef.current = setTimeout(() => {
      setStableBounds(newBounds);
    }, BOUNDS_SETTLE_MS);
  }, [viewState, containerSize.width, containerSize.height]); // eslint-disable-line react-hooks/exhaustive-deps

  const positionBounds = useMemo(() => {
    if (!USE_BOUNDED_POSITION_FETCH) return undefined;
    if (!stableBounds) return undefined;

    if (viewState.zoom < POSITION_BOUNDS_ZOOM_THRESHOLD) return undefined;

    const spanLng = stableBounds.east - stableBounds.west;
    const spanLat = stableBounds.north - stableBounds.south;
    // 200% padding: load positions in a 5× viewport area in each direction.
    // This ensures devices that were in a cluster near the edge are still
    // present in rawCoords when the cluster breaks apart at the next zoom step.
    const padLng = spanLng * POSITION_BOUNDS_PADDING_FACTOR;
    const padLat = spanLat * POSITION_BOUNDS_PADDING_FACTOR;

    const left = Math.max(-180, stableBounds.west - padLng);
    const right = Math.min(180, stableBounds.east + padLng);
    const bottom = Math.max(-85, stableBounds.south - padLat);
    const top = Math.min(85, stableBounds.north + padLat);

    return {
      bounds: { left, bottom, right, top },
    };
  }, [stableBounds, viewState.zoom]);

  const { data: rawCoords = [] } = useLastKnownPositions(positionBounds, {
    refetchIntervalMs: POSITION_REFETCH_MS,
  });

  const retainedCoordsRef = useRef<
    globalThis.Map<string, { lng: number; lat: number; isDriving: boolean; seenAt: number }>
  >(new globalThis.Map());
  const [stableCoords, setStableCoords] = useState<Coord[]>([]);

  useEffect(() => {
    const now = Date.now();
    const retained = retainedCoordsRef.current;

    for (const [deviceId, entry] of retained.entries()) {
      if (now - entry.seenAt > POSITION_RETENTION_MS) {
        retained.delete(deviceId);
      }
    }

    for (const [lng, lat, id, isDriving] of rawCoords) {
      if (!id || !Number.isFinite(lng) || !Number.isFinite(lat)) continue;
      retained.set(id, {
        lng: normalizeLng(lng),
        lat: clamp(lat, MIN_MAP_LAT, MAX_MAP_LAT),
        isDriving: !!isDriving,
        seenAt: now,
      });
    }

    const merged = Array.from(retained.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([deviceId, entry]) => [entry.lng, entry.lat, deviceId, entry.isDriving] as Coord);
    setStableCoords(merged);
  }, [rawCoords]);

  /**
   * The colour of the cluster bubbles.
   *
   * A bubble stands for several vehicles, so it can only carry a group's colour when exactly one
   * group is set up; with two, it would be claiming one group's colour for vehicles of both.
   */
  const clusterColor =
    (groupStyles.groups.length === 1 ? groupStyles.groups[0].color : "") ||
    settings.vehicleIconColor ||
    primaryColor;

  // ─── Selection & follow ─────────────────────────────────────────
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const followingDeviceRef = useRef<string | null>(null);

  // ─── Clustering ─────────────────────────────────────────────────
  const { clusters, getClusterExpansionZoom } = useCluster(stableCoords, viewState.zoom, {
    enabled: settings.clusteringEnabled,
    radius: settings.clusterRadiusPx,
  });

  // ─── Data fetching ───────────────────────────────────────────────
  const visibleStatusIds = useRef<string[]>([]);
  /**
   * Every vehicle inside the current view, whether the map draws it on its own or folded into a
   * cluster bubble. The vehicle list shows all of them, so all of them are asked about — the
   * owner chose that over a list that empties itself as soon as the map is zoomed out.
   */
  const stableVisibleIds = useMemo(() => {
    const visible = new Set<string>();
    for (const [longitude, latitude, deviceId] of stableCoords) {
      if (
        !stableBounds ||
        (longitude >= stableBounds.west &&
          longitude <= stableBounds.east &&
          latitude >= stableBounds.south &&
          latitude <= stableBounds.north)
      ) {
        visible.add(deviceId);
      }
    }
    const sorted = Array.from(visible).sort();
    const key = sorted.join(",");
    const prevKey = visibleStatusIds.current.join(",");
    if (key === prevKey) return visibleStatusIds.current;
    visibleStatusIds.current = sorted;
    return sorted;
  }, [stableCoords, stableBounds]);
  const { data: statusInfoMap = {}, dataUpdatedAt: statusInfoMapUpdatedAt } = useStatusInfosByIds(
    stableVisibleIds,
    MAP_DIAGNOSTICS,
    STATUS_INFO_REFETCH_MS,
  );
  // Names for every vehicle on screen, not just the hovered one: the labels and the list need all
  // of them at once, and the tooltip's lazy per-device fetch would be one request per vehicle.
  const { data: visibleDevices } = useDevicesByIds(stableVisibleIds, DEVICE_FIELDS);
  const groupIdsByDevice = useMemo(() => {
    const groupIds = new globalThis.Map<string, string[]>();
    for (const device of visibleDevices ?? []) {
      groupIds.set(
        device.id,
        (device.groups ?? []).flatMap(group => (group.id ? [group.id] : [])),
      );
    }
    return groupIds;
  }, [visibleDevices]);

  const deviceNamesById = useMemo(() => {
    const names: Record<string, string | undefined> = {};
    for (const device of visibleDevices ?? []) {
      names[device.id] = device.name;
    }
    return names;
  }, [visibleDevices]);

  const lastBearingByDeviceRef = useRef(new globalThis.Map<string, number>());

  useEffect(() => {
    for (const [deviceId, info] of Object.entries(statusInfoMap)) {
      if (typeof info?.bearing === "number" && Number.isFinite(info.bearing)) {
        lastBearingByDeviceRef.current.set(deviceId, normalizeBearing(info.bearing));
      }
    }
  }, [statusInfoMap]);

  const speedByDeviceId = useMemo(() => {
    const speeds: Record<string, number | undefined> = {};
    for (const [deviceId, info] of Object.entries(statusInfoMap)) {
      speeds[deviceId] = typeof info?.speed === "number" ? info.speed : undefined;
    }
    return speeds;
  }, [statusInfoMap]);

  const animatedCoords = useAnimatedPositions(stableCoords, {
    fallbackDurationMs: POSITION_ANIMATION_FALLBACK_MS,
    minDurationMs: 1200,
    maxDurationMs: 45000,
    speedByDeviceId,
  });

  const animatedPositionMap = useMemo(() => toPositionMap(animatedCoords), [animatedCoords]);

  // For icon state, prefer fast status polling and fall back to last known position flags.
  const isDrivingMap = useMemo(() => {
    const m: Record<string, boolean> = {};
    for (const [, , id, driving] of stableCoords) {
      m[id] = driving;
    }
    for (const [id, info] of Object.entries(statusInfoMap)) {
      if (typeof info?.isDriving === "boolean") {
        m[id] = info.isDriving;
      }
    }
    return m;
  }, [stableCoords, statusInfoMap]);

  const drivingDisplayStateRef = useRef<
    globalThis.Map<string, { value: boolean; pendingValue?: boolean; pendingSince?: number }>
  >(new globalThis.Map());
  const [displayDrivingMap, setDisplayDrivingMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const now = Date.now();
    const displayState = drivingDisplayStateRef.current;
    const ids = new Set<string>([...displayState.keys(), ...Object.keys(isDrivingMap)]);

    for (const id of ids) {
      const rawValue = isDrivingMap[id];
      if (rawValue == null) {
        displayState.delete(id);
        continue;
      }

      const entry = displayState.get(id);
      if (!entry) {
        displayState.set(id, { value: rawValue });
        continue;
      }

      if (entry.value === rawValue) {
        entry.pendingValue = undefined;
        entry.pendingSince = undefined;
        continue;
      }

      if (entry.pendingValue !== rawValue) {
        entry.pendingValue = rawValue;
        entry.pendingSince = now;
        continue;
      }

      if (entry.pendingSince != null && now - entry.pendingSince >= DRIVING_STATE_SWITCH_DELAY_MS) {
        entry.value = rawValue;
        entry.pendingValue = undefined;
        entry.pendingSince = undefined;
      }
    }

    const nextDisplayMap: Record<string, boolean> = {};
    for (const [id, entry] of displayState.entries()) {
      nextDisplayMap[id] = entry.value;
    }
    setDisplayDrivingMap(nextDisplayMap);
  }, [isDrivingMap]);

  // ─── Follow selected device ─────────────────────────────────────
  // When a device is selected, keep the map centered on it and rotated to its bearing
  useEffect(() => {
    const deviceId = followingDeviceRef.current;
    if (!deviceId) return;
    const pos = animatedPositionMap.get(deviceId);
    if (!pos) return;
    const isDriving = displayDrivingMap[deviceId] ?? isDrivingMap[deviceId] ?? false;
    const latestBearing = statusInfoMap[deviceId]?.bearing;
    const targetBearing =
      typeof latestBearing === "number" && Number.isFinite(latestBearing)
        ? normalizeBearing(latestBearing)
        : (lastBearingByDeviceRef.current.get(deviceId) ?? 0);
    setViewState(prev => {
      const deltaLng = pos[0] - prev.longitude;
      const deltaLat = pos[1] - prev.latitude;
      const deltaBearing = isDriving ? shortestAngleDelta(prev.bearing, targetBearing) : 0;

      if (
        Math.abs(deltaLng) < FOLLOW_POSITION_EPSILON &&
        Math.abs(deltaLat) < FOLLOW_POSITION_EPSILON &&
        Math.abs(deltaBearing) < FOLLOW_BEARING_EPSILON
      ) {
        return prev;
      }

      return {
        ...prev,
        longitude: prev.longitude + deltaLng * FOLLOW_POSITION_LERP_FACTOR,
        latitude: prev.latitude + deltaLat * FOLLOW_POSITION_LERP_FACTOR,
        bearing: normalizeBearing(prev.bearing + deltaBearing * FOLLOW_BEARING_LERP_FACTOR),
        transitionDuration: undefined,
        transitionInterpolator: undefined,
      };
    });
  }, [animatedPositionMap, statusInfoMap, isDrivingMap, displayDrivingMap]);

  // ─── Lazy device name cache for tooltips ─────────────────────────
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();
  const deviceNameCache = useRef(new globalThis.Map<string, string>());
  const pendingNameFetches = useRef(new Set<string>());
  // Counter to trigger re-render when a name loads
  const [, setNameCacheVersion] = useState(0);

  const fetchDeviceName = useCallback(
    (deviceId: string) => {
      if (deviceNameCache.current.has(deviceId) || pendingNameFetches.current.has(deviceId)) return;
      pendingNameFetches.current.add(deviceId);

      queryClient
        .fetchQuery({
          queryKey: ["device", activeSessionId, deviceId],
          queryFn: ({ signal }) =>
            geotabClient
              .get("Device", { id: deviceId }, 1, signal)
              .then(devices => devices[0] ?? null),
          staleTime: 5 * 60_000,
        })
        .then(device => {
          if (device?.name) {
            deviceNameCache.current.set(deviceId, device.name);
            // Trigger re-render so tooltip updates from skeleton → name
            setNameCacheVersion(c => c + 1);
          }
        })
        .finally(() => {
          pendingNameFetches.current.delete(deviceId);
        });
    },
    [queryClient, activeSessionId, geotabClient],
  );

  // ─── Fixed tooltip state ────────────────────────────────────────
  const [tooltipInfo, setTooltipInfo] = useState<TooltipInfo | null>(null);

  const handleHover = useCallback(
    ({ object, x, y }: { object?: ClusterOrPoint; x: number; y: number }) => {
      if (!object) {
        setTooltipInfo(null);
        return;
      }
      if (isCluster(object)) {
        setTooltipInfo({
          x,
          y,
          isCluster: true,
          clusterCount: object.properties.point_count,
        });
        return;
      }
      const deviceId = object.properties.deviceId;
      if (!deviceNameCache.current.has(deviceId)) {
        fetchDeviceName(deviceId);
      }
      setTooltipInfo({ x, y, isCluster: false, deviceId });
    },
    [fetchDeviceName],
  );

  const tooltipText = (() => {
    if (!tooltipInfo) return null;

    if (tooltipInfo.isCluster) {
      return t("map.vehicles", { count: tooltipInfo.clusterCount ?? 0 });
    }

    const deviceId = tooltipInfo.deviceId;
    if (!deviceId) return null;

    const deviceName = deviceNameCache.current.get(deviceId);
    return deviceName ?? null;
  })();

  const { clusterData, pointData } = useMemo(() => {
    const cd: ClusterOrPoint[] = [];
    const pd: ClusterOrPoint[] = [];
    for (const c of clusters) {
      if (isCluster(c)) {
        cd.push(c);
      } else {
        pd.push(c);
      }
    }
    return { clusterData: cd, pointData: pd };
  }, [clusters]);

  const overlappingPointOffsets = useMemo(() => {
    const offsets = new globalThis.Map<string, [number, number]>();
    if (pointData.length < 2) return offsets;

    const worldPixels = 256 * Math.pow(2, viewState.zoom);
    if (!Number.isFinite(worldPixels) || worldPixels <= 0) return offsets;

    function toWorldPixels(lng: number, lat: number): [number, number] {
      const safeLat = clamp(lat, MIN_MAP_LAT, MAX_MAP_LAT);
      const latRad = (safeLat * Math.PI) / 180;
      const x = ((normalizeLng(lng) + 180) / 360) * worldPixels;
      const y =
        ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * worldPixels;
      return [x, y];
    }

    type OverlapGroup = { ids: string[]; centerX: number; centerY: number; count: number };

    const groups: OverlapGroup[] = [];
    const cellToGroupIndexes = new globalThis.Map<string, number[]>();
    const baseCoordById = new globalThis.Map<string, [number, number]>();

    const registerGroupCell = (groupIndex: number, cellX: number, cellY: number) => {
      const key = `${cellX}:${cellY}`;
      const existing = cellToGroupIndexes.get(key);
      if (!existing) {
        cellToGroupIndexes.set(key, [groupIndex]);
        return;
      }
      if (!existing.includes(groupIndex)) {
        existing.push(groupIndex);
      }
    };

    for (const point of pointData) {
      const [lng, lat] = point.geometry.coordinates as [number, number];
      const deviceId = point.properties.deviceId;
      baseCoordById.set(deviceId, [lng, lat]);

      const [x, y] = toWorldPixels(lng, lat);
      const cellX = Math.floor(x / OVERLAP_DETECTION_PX);
      const cellY = Math.floor(y / OVERLAP_DETECTION_PX);

      const candidateIndexes = new Set<number>();
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const candidate = cellToGroupIndexes.get(`${cellX + dx}:${cellY + dy}`);
          if (!candidate) continue;
          for (const index of candidate) candidateIndexes.add(index);
        }
      }

      let bestGroupIndex = -1;
      let bestDistance = Number.POSITIVE_INFINITY;
      for (const index of candidateIndexes) {
        const group = groups[index];
        const distance = Math.hypot(x - group.centerX, y - group.centerY);
        if (distance <= OVERLAP_DETECTION_PX && distance < bestDistance) {
          bestDistance = distance;
          bestGroupIndex = index;
        }
      }

      if (bestGroupIndex >= 0) {
        const group = groups[bestGroupIndex];
        group.ids.push(deviceId);
        group.count += 1;
        group.centerX += (x - group.centerX) / group.count;
        group.centerY += (y - group.centerY) / group.count;
        registerGroupCell(bestGroupIndex, cellX, cellY);
      } else {
        const groupIndex = groups.length;
        groups.push({ ids: [deviceId], centerX: x, centerY: y, count: 1 });
        registerGroupCell(groupIndex, cellX, cellY);
      }
    }

    for (const group of groups) {
      if (group.ids.length < 2) continue;
      group.ids.sort();
      const spreadPx = OVERLAP_SPREAD_PX + Math.min(12, Math.log2(group.ids.length) * 3);
      const angleStep = (Math.PI * 2) / group.ids.length;

      for (let i = 0; i < group.ids.length; i++) {
        const deviceId = group.ids[i];
        const base = baseCoordById.get(deviceId);
        if (!base) continue;
        const [, lat] = base;
        const angle = i * angleStep;
        const dxPx = Math.cos(angle) * spreadPx;
        const dyPx = Math.sin(angle) * spreadPx;

        const lngOffset = (dxPx * 360) / worldPixels;
        const latCos = Math.max(0.2, Math.cos((lat * Math.PI) / 180));
        const latOffset = ((-dyPx * 360) / worldPixels) * latCos;
        offsets.set(deviceId, [lngOffset, latOffset]);
      }
    }

    return offsets;
  }, [pointData, viewState.zoom]);

  const labelPoints = useMemo(
    () =>
      pointData.map(point => ({
        deviceId: point.properties.deviceId,
        coordinates: point.geometry.coordinates as [number, number],
      })),
    [pointData],
  );

  // Cluster bubbles carry no name of their own but hide their count when a name lands on them.
  const labelObstacles = useMemo(
    () =>
      clusterData.map(cluster => ({
        coordinates: cluster.geometry.coordinates as [number, number],
        halfSizePx: clusterIconSize(isCluster(cluster) ? cluster.properties.point_count : 1) / 2,
      })),
    [clusterData],
  );

  // What the map knows about each visible vehicle. The labels and the vehicle list both read this,
  // so the facts behind them cannot drift apart.
  const fuelCellDeviceIds = useFuelCellDeviceIds();

  const assetRows = useMapAssetRows({
    deviceIds: stableVisibleIds,
    names: deviceNamesById,
    statusInfoMap,
    statusInfoMapUpdatedAt,
    drivingById: displayDrivingMap,
    fuelCellDeviceIds,
  });

  // The last positions actually reported, not the smoothed ones: these feed the address lookup,
  // whose query key would otherwise change sixty times a second.
  const positionById = useMemo(() => toPositionMap(stableCoords), [stableCoords]);

  /**
   * What each name label carries besides the name.
   *
   * The drive-type word is only put in when the reader asked for it, so the layout does not
   * reserve room for something that is not drawn.
   */
  const labelBadgeById = useMemo(() => {
    const badges = new globalThis.Map<string, AssetLabelBadge>();
    for (const row of assetRows) {
      badges.set(row.deviceId, {
        driveType: row.driveType,
        energyPercent: row.energyPercent,
        isCharging: row.isCharging,
        powertrainLabel: settings.showPowertrainBadges
          ? POWERTRAIN_LABELS[row.driveType]
          : undefined,
      });
    }
    return badges;
  }, [assetRows, settings.showPowertrainBadges]);

  const assetLabels = useAssetLabels({
    enabled: settings.showAssetNames,
    points: labelPoints,
    obstacles: labelObstacles,
    badgeById: labelBadgeById,
    animatedPositions: animatedPositionMap,
    offsets: overlappingPointOffsets,
    names: deviceNamesById,
    viewState,
    containerSize,
    iconSizePx: settings.vehicleIconSizePx,
  });

  // ─── Layers ──────────────────────────────────────────────────────

  /**
   * How a vehicle is drawn: its group's own symbol if that group has one, otherwise the built-in
   * arrow or square in the group's colour.
   *
   * An uploaded symbol is drawn as its author drew it. Recolouring arbitrary artwork to one colour
   * turns a two-colour logo into a silhouette, and the person who uploaded it chose how it looks.
   */
  /**
   * The colour and symbol each vehicle is drawn in, worked out once.
   *
   * Four things need this — the marker, its heading arrow, the ring around it and the row in the
   * list — and each used to search the group styles for itself, some of them twice over. That is
   * a linear search per vehicle per draw, for an answer that only changes when a group's colour
   * does.
   */
  const styleByDevice = useMemo(() => {
    const styles = new globalThis.Map<string, { color: string; svg?: string }>();
    for (const [deviceId, groupIds] of groupIdsByDevice) {
      const style = groupStyleOf(groupIds, groupStyles.groups);
      styles.set(deviceId, {
        color: style?.color || settings.vehicleIconColor || primaryColor,
        svg: style?.svg,
      });
    }
    return styles;
  }, [groupIdsByDevice, groupStyles.groups, settings.vehicleIconColor, primaryColor]);

  const fallbackColor = settings.vehicleIconColor || primaryColor;

  /**
   * The layer colours, as deck.gl wants them.
   *
   * Each of these parses a hex string and allocates an array. Worked out inside the layers, that
   * happened on every render — sixty times a second while anything is moving — for colours that
   * only change when the reader touches a slider.
   */
  const layerColors = useMemo(
    () => ({
      zoneFill: hexToRgba(primaryColor, 8, primaryColor),
      zoneLine: hexToRgba(primaryColor, 45, primaryColor),
      ringFill: hexToRgba(settings.ringFillColor, settings.ringFillOpacity, "#ffffff"),
      ringBorder: hexToRgba(settings.ringBorderColor, settings.ringBorderOpacity, primaryColor),
    }),
    [
      primaryColor,
      settings.ringFillColor,
      settings.ringFillOpacity,
      settings.ringBorderColor,
      settings.ringBorderOpacity,
    ],
  );

  const colorOfDevice = useCallback(
    (deviceId: string) => styleByDevice.get(deviceId)?.color ?? fallbackColor,
    [styleByDevice, fallbackColor],
  );

  /** The heading a vehicle last reported, falling back to the last one seen. */
  const bearingOfDevice = useCallback(
    (deviceId: string) => {
      const latest = statusInfoMap[deviceId]?.bearing;
      return typeof latest === "number" && Number.isFinite(latest)
        ? normalizeBearing(latest)
        : (lastBearingByDeviceRef.current.get(deviceId) ?? 0);
    },
    [statusInfoMap],
  );

  const symbolOfDevice = useCallback(
    (deviceId: string) => styleByDevice.get(deviceId)?.svg,
    [styleByDevice],
  );

  const iconOfDevice = useCallback(
    (deviceId: string, isDriving: boolean) => {
      const style = styleByDevice.get(deviceId);
      const color = style?.color ?? fallbackColor;

      if (style?.svg) {
        return groupSymbolIcon(style.svg, color);
      }

      const icons = getVehicleIcons(color);
      return isDriving ? icons.driving : icons.stopped;
    },
    [styleByDevice, fallbackColor],
  );

  /**
   * Geotab's zones, under everything else.
   *
   * Faint on purpose: they are context for where the vehicles are, not the subject. Only fetched
   * while they are switched on.
   */
  const zoneLayer = new PolygonLayer({
    id: "zones",
    data: zoneOutlines,
    visible: settings.showZones,
    getPolygon: (zone: ZoneOutline) => zone.points.map(point => [point.x, point.y]),
    stroked: true,
    filled: true,
    lineWidthUnits: "pixels",
    getLineWidth: 1,
    getFillColor: layerColors.zoneFill,
    getLineColor: layerColors.zoneLine,
    pickable: false,
  });

  /**
   * Half the icon, plus whatever distance was asked for. So the ring sits exactly around the
   * symbol at a distance of nothing, grows and shrinks with it, and the heading marker has
   * somewhere definite to sit.
   */
  const ringRadiusPx = Math.max(2, settings.vehicleIconSizePx / 2 + settings.ringPaddingPx);

  /**
   * The ring under the vehicles.
   *
   * Its own layer beneath the icons rather than part of them: the icons are images and a ring
   * whose width, colour and transparency are all adjustable would mean rebuilding an image on
   * every drag of a slider.
   */
  const vehicleRingLayer = new ScatterplotLayer({
    id: "vehicle-rings",
    data: pointData,
    getPosition: (d: ClusterOrPoint) => {
      const animated = animatedPositionMap.get(d.properties.deviceId);
      const base = animated ?? (d.geometry.coordinates as [number, number]);
      const offset = overlappingPointOffsets.get(d.properties.deviceId);
      if (!offset) return base;
      return [base[0] + offset[0], base[1] + offset[1]];
    },
    radiusUnits: "pixels",
    getRadius: ringRadiusPx,
    stroked: true,
    filled: true,
    lineWidthUnits: "pixels",
    getLineWidth: settings.ringBorderWidthPx,
    getFillColor: layerColors.ringFill,
    getLineColor: layerColors.ringBorder,
    updateTriggers: {
      getPosition: [animatedPositionMap, overlappingPointOffsets],
      getRadius: [settings.vehicleIconSizePx, settings.ringPaddingPx],
      getLineWidth: [settings.ringBorderWidthPx],
      getFillColor: [settings.ringFillColor, settings.ringFillOpacity],
      getLineColor: [settings.ringBorderColor, settings.ringBorderOpacity, primaryColor],
    },
  });

  const vehicleIconLayer = new IconLayer({
    id: "vehicle-icons",
    data: pointData,
    getPosition: (d: ClusterOrPoint) => {
      const animated = animatedPositionMap.get(d.properties.deviceId);
      const base = animated ?? (d.geometry.coordinates as [number, number]);
      const offset = overlappingPointOffsets.get(d.properties.deviceId);
      if (!offset) return base;
      return [base[0] + offset[0], base[1] + offset[1]];
    },
    getSize: settings.vehicleIconSizePx,
    sizeUnits: "pixels",
    getAngle: (d: ClusterOrPoint) => {
      const deviceId = d.properties.deviceId;
      const isDriving = displayDrivingMap[deviceId] ?? isDrivingMap[deviceId] ?? d.properties.isDriving;
      // A group's own symbol stays upright; its heading is shown by the marker on the ring.
      if (!isDriving || symbolOfDevice(deviceId)) return 0;

      return vehicleIconAngle(bearingOfDevice(deviceId), viewState.bearing);
    },
    pickable: true,
    getIcon: (d: ClusterOrPoint) => {
      const deviceId = d.properties.deviceId;
      const isDriving =
        displayDrivingMap[deviceId] ?? isDrivingMap[deviceId] ?? d.properties.isDriving;
      return iconOfDevice(deviceId, isDriving);
    },
    onClick: ({ object }: { object?: ClusterOrPoint }) => {
      if (object && !isCluster(object)) {
        const deviceId = object.properties.deviceId;
        setSelectedDeviceId(deviceId);
        followingDeviceRef.current = deviceId;
        const pos = animatedPositionMap.get(deviceId);
        if (pos) {
          const latestBearing = statusInfoMap[deviceId]?.bearing;
          const deviceBearing =
            typeof latestBearing === "number" && Number.isFinite(latestBearing)
              ? normalizeBearing(latestBearing)
              : (lastBearingByDeviceRef.current.get(deviceId) ?? 0);
          flyTo(pos[0], pos[1], Math.max(viewState.zoom, 16), 2000, deviceBearing);
        }
      }
    },
    onHover: handleHover,
    updateTriggers: {
      getPosition: [animatedPositionMap, overlappingPointOffsets],
      getIcon: [iconOfDevice, isDrivingMap, displayDrivingMap],
      getAngle: [statusInfoMap, isDrivingMap, displayDrivingMap, viewState.bearing],
      // Without this the icons keep whatever size they were first drawn at, and the size slider
      // appears to move only the ring behind them.
      getSize: [settings.vehicleIconSizePx],
    },
  });

  /**
   * The heading marker, for vehicles drawn with their group's own symbol.
   *
   * Those symbols do not turn, so the direction goes on the ring around them: the marker is
   * pushed out to the ring's edge along the heading and rotated to point that way. Vehicles that
   * are standing, and vehicles drawn with the built-in arrow, get a marker of no size — that is
   * cheaper than filtering the data, which would rebuild the layer on every change of driving
   * state.
   */
  const headingMarkerLayer = new IconLayer({
    id: "vehicle-heading-markers",
    data: pointData,
    getPosition: (d: ClusterOrPoint) => {
      const animated = animatedPositionMap.get(d.properties.deviceId);
      const base = animated ?? (d.geometry.coordinates as [number, number]);
      const offset = overlappingPointOffsets.get(d.properties.deviceId);
      if (!offset) return base;
      return [base[0] + offset[0], base[1] + offset[1]];
    },
    getSize: (d: ClusterOrPoint) => {
      const deviceId = d.properties.deviceId;
      const isDriving =
        displayDrivingMap[deviceId] ?? isDrivingMap[deviceId] ?? d.properties.isDriving;
      return isDriving && symbolOfDevice(deviceId) ? HEADING_MARKER_SIZE_PX : 0;
    },
    sizeUnits: "pixels",
    getIcon: (d: ClusterOrPoint) => getHeadingMarker(colorOfDevice(d.properties.deviceId)),
    getAngle: (d: ClusterOrPoint) =>
      vehicleIconAngle(bearingOfDevice(d.properties.deviceId), viewState.bearing),
    getPixelOffset: (d: ClusterOrPoint) => {
      // Clockwise from straight up on screen, which is where the marker has to sit.
      const screenAngle =
        ((bearingOfDevice(d.properties.deviceId) - viewState.bearing) * Math.PI) / 180;
      const distance = ringRadiusPx + HEADING_MARKER_SIZE_PX / 2;
      // The y axis of a pixel offset points down, hence the minus.
      return [Math.sin(screenAngle) * distance, -Math.cos(screenAngle) * distance];
    },
    updateTriggers: {
      getPosition: [animatedPositionMap, overlappingPointOffsets],
      getSize: [isDrivingMap, displayDrivingMap, symbolOfDevice],
      getIcon: [colorOfDevice],
      getAngle: [statusInfoMap, viewState.bearing],
      getPixelOffset: [statusInfoMap, viewState.bearing, ringRadiusPx],
    },
  });

  const clusterIconLayer = new IconLayer({
    id: "cluster-icons",
    data: clusterData,
    getPosition: (d: ClusterOrPoint) => d.geometry.coordinates as [number, number],
    getSize: (d: ClusterOrPoint) =>
      (clusterIconSize(isCluster(d) ? d.properties.point_count : 1) *
        settings.clusterIconScalePercent) /
      100,
    sizeUnits: "pixels",
    pickable: true,
    getIcon: (d: ClusterOrPoint) => {
      const count = isCluster(d) ? d.properties.point_count : 1;
      return {
        url: getClusterIcon(count, clusterColor),
        width: 128,
        height: 128,
      };
    },
    onClick: ({ object }: { object?: ClusterOrPoint }) => {
      if (object && isCluster(object)) {
        const clusterId = object.properties.cluster_id;
        const expansionZoom = getClusterExpansionZoom(clusterId);
        const [lng, lat] = object.geometry.coordinates;
        flyTo(lng, lat, Math.min(20, Math.max(viewState.zoom + 1, expansionZoom)), 500);
      }
    },
    onHover: handleHover,
    updateTriggers: {
      getIcon: [clusterData, clusterColor],
      getSize: [clusterData, settings.clusterIconScalePercent],
    },
  });

  // ─── Search handlers ─────────────────────────────────────────────

  const handleDeviceSelect = useCallback(
    (deviceId: string, lng: number, lat: number) => {
      setSelectedDeviceId(deviceId);
      followingDeviceRef.current = deviceId;
      flyTo(lng, lat, 16);
    },
    [flyTo],
  );

  const handleLocationSelect = useCallback(
    (lng: number, lat: number) => {
      setSelectedDeviceId(null);
      followingDeviceRef.current = null;
      flyTo(lng, lat, 14);
    },
    [flyTo],
  );

  const handleListSelect = useCallback(
    (deviceId: string) => {
      const position = positionById.get(deviceId);
      if (!position) return;
      handleDeviceSelect(deviceId, position[0], position[1]);
    },
    [positionById, handleDeviceSelect],
  );

  const handleCloseVehicleInfo = useCallback(() => {
    setSelectedDeviceId(null);
    followingDeviceRef.current = null;
  }, []);

  /**
   * Frames the whole fleet.
   *
   * Every vehicle whose position we hold, which is exactly the set the group filter in the header
   * allows — so what the reader is filtered to is what gets framed.
   */
  const handleFitFleet = useCallback(() => {
    const bounds = boundsOfPositions(positionById.values());
    if (!bounds) {
      return;
    }

    followingDeviceRef.current = null;
    setViewState(previous => ({
      ...previous,
      ...viewportForBounds(bounds, containerSize.width, containerSize.height),
      transitionDuration: 800,
      transitionInterpolator: new FlyToInterpolator(),
    }));
  }, [positionById, containerSize.width, containerSize.height]);

  const handleResetNorth = useCallback(() => {
    followingDeviceRef.current = null;
    setViewState(prev => ({
      ...prev,
      bearing: 0,
      transitionDuration: 500,
      transitionInterpolator: new FlyToInterpolator(),
    }));
  }, []);

  // Stop following when user manually pans/zooms
  const handleViewStateChangeWithFollow = useCallback(
    ({ viewState: vs, interactionState }: ViewStateChange) => {
      // If user is actively interacting (not a programmatic fly-to), stop following
      if (interactionState.isPanning || interactionState.isZooming) {
        followingDeviceRef.current = null;
      }
      // deck.gl reports the next view state with optional pitch/bearing; the map
      // controller always supplies both, so fall back to the current values.
      if (!("longitude" in vs)) return;
      setViewState(prev => ({
        ...vs,
        pitch: vs.pitch ?? prev.pitch,
        bearing: vs.bearing ?? prev.bearing,
      }));
    },
    [],
  );

  // ─── Render ──────────────────────────────────────────────────────

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%", position: "relative" }}>
      <DeckGL
        deviceProps={{ type: "webgl" }}
        viewState={viewState}
        onViewStateChange={handleViewStateChangeWithFollow}
        controller={true}
        layers={[zoneLayer, vehicleRingLayer, vehicleIconLayer, headingMarkerLayer, clusterIconLayer]}
        style={{ width: "100%", height: "100%" }}
      >
        <Map mapStyle={MAP_STYLES[settings.mapStyle]}>
          {/*
            Layers of their own rather than part of the chosen basemap, so the traffic can be
            shown over any of them — including the plain OpenStreetMap one, which is what a
            dispatcher watching a jam actually wants under it.
          */}
          {trafficLayers.map(({ key, tiles }) => (
            <Source key={key} id={`traffic-${key}`} type="raster" tiles={tiles} tileSize={256}>
              <Layer id={`traffic-${key}`} type="raster" paint={{ "raster-opacity": 0.75 }} />
            </Source>
          ))}
        </Map>
      </DeckGL>

      <AssetLabelOverlay
        labels={assetLabels}
        badgeById={labelBadgeById}
        color={settings.assetNameColor || themeColors.labelText}
        backgroundColor={settings.assetNameBackgroundColor || themeColors.labelBackground}
      />

      <FixedTooltip info={tooltipInfo} text={tooltipText} />

      <MapControlStack>
        <CompassButton bearing={viewState.bearing} onClick={handleResetNorth} />
        <MapToggleButton
          label={
            settings.showAssetNames
              ? t("map.hideAssetNames", "Fahrzeugnamen ausblenden")
              : t("map.showAssetNames", "Fahrzeugnamen anzeigen")
          }
          active={settings.showAssetNames}
          icon={
            <LabelIcon
              fontSize="small"
              color={settings.showAssetNames ? "primary" : "disabled"}
            />
          }
          onClick={() => updateSettings({ showAssetNames: !settings.showAssetNames })}
        />
        <MapToggleButton
          label={t("map.fitFleet", "Alle Fahrzeuge zeigen")}
          icon={<ZoomOutMapIcon fontSize="small" color="action" />}
          onClick={handleFitFleet}
        />
        <MapToggleButton
          label={t("map.settings.title", "Karteneinstellungen")}
          active={settingsOpen}
          icon={<SettingsIcon fontSize="small" color={settingsOpen ? "primary" : "disabled"} />}
          onClick={() => setSettingsOpen(open => !open)}
        />
      </MapControlStack>

      {settingsOpen ? (
        <MapSettingsPanel
          settings={settings}
          update={updateSettings}
          themeColors={themeColors}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}

      <MapSearchBar
        devicePositions={animatedPositionMap}
        onDeviceSelect={handleDeviceSelect}
        onLocationSelect={handleLocationSelect}
      />

      <VehicleListPanel
        rows={assetRows}
        positionById={positionById}
        groupIdsByDevice={groupIdsByDevice}
        colorOfDevice={colorOfDevice}
        showZones={settings.showZones}
        containerWidth={containerSize.width}
        selectedDeviceId={selectedDeviceId}
        onSelect={handleListSelect}
      />

      {selectedDeviceId && (
        <VehicleInfoPanel
          deviceId={selectedDeviceId}
          statusInfoMap={statusInfoMap}
          statusInfoMapUpdatedAt={statusInfoMapUpdatedAt}
          isDrivingMap={displayDrivingMap}
          onClose={handleCloseVehicleInfo}
        />
      )}
    </div>
  );
};

export default LiveMapView;
